import { oklchToLinearSrgb, type Oklch } from "./lightness.ts";
import { clamp } from "./utils.ts";

// Part of this module's public surface: `hexToOklch`/`oklchToHex`/`parseOklch`
// all speak it, so consumers of those need the name too.
export type { Oklch };

const HEX_COLOR = /^#(?:[\da-f]{3}|[\da-f]{6})$/i;


function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR.test(value);
}

export function normalizeHexColor(value: string): string | null {
  const candidate = value.trim();
  if (!isHexColor(candidate)) return null;
  if (candidate.length === 4) {
    return `#${candidate
      .slice(1)
      .split("")
      .map((channel) => channel + channel)
      .join("")}`.toLowerCase();
  }
  return candidate.toLowerCase();
}

function srgbToLinear(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045
    ? value / 12.92
    : ((value + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(channel: number): number {
  const value = clamp(channel, 0, 1);
  return value <= 0.0031308
    ? value * 12.92
    : 1.055 * value ** (1 / 2.4) - 0.055;
}

export function hexToOklch(value: string): Oklch | null {
  const hex = normalizeHexColor(value);
  if (!hex) return null;
  const integer = Number.parseInt(hex.slice(1), 16);
  const red = srgbToLinear((integer >> 16) & 255);
  const green = srgbToLinear((integer >> 8) & 255);
  const blue = srgbToLinear(integer & 255);

  const l = Math.cbrt(
    0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue,
  );
  const m = Math.cbrt(
    0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue,
  );
  const s = Math.cbrt(
    0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue,
  );
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const b = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const chroma = Math.sqrt(a * a + b * b);
  const hue = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l: lightness * 100, c: chroma, h: chroma < 0.0001 ? 0 : hue };
}

/**
 * Parses the `oklch(L% C H)` / `oklch(L% C H / A)` form `formatOklch` writes
 * and `tokens.css` is authored in — the shape a token read off the document
 * comes back as. Returns null for anything else (a `color-mix()`, a `var()`,
 * an empty read), never a guess. Alpha is dropped: a veil's transparency has
 * no meaning away from the surface it was authored over.
 */
export function parseOklch(value: string): Oklch | null {
  const match = value
    .trim()
    .match(
      /^oklch\(\s*(-?[\d.]+)%\s+(-?[\d.]+)\s+(-?[\d.]+)(?:\s*\/\s*[\d.]+%?)?\s*\)$/i,
    );
  if (!match) return null;
  const [l, c, h] = [match[1], match[2], match[3]].map(Number);
  if (![l, c, h].every(Number.isFinite)) return null;
  return { l: l!, c: c!, h: ((h! % 360) + 360) % 360 };
}

export function oklchToHex(color: Oklch): string {
  const channels = oklchToLinearSrgb(color).map((channel) =>
    Math.round(linearToSrgb(channel) * 255),
  );
  return `#${channels
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

export function hslTokenToHex(value: string): string | null {
  const parts = value.replace(/%/g, "").trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }
  // The length-3 + all-finite guard above makes every index a real number.
  const hue = ((parts[0]! % 360) + 360) % 360;
  const saturation = clamp(parts[1]! / 100, 0, 1);
  const lightness = clamp(parts[2]! / 100, 0, 1);
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const offset = lightness - chroma / 2;
  const [red, green, blue] =
    hue < 60
      ? [chroma, x, 0]
      : hue < 120
        ? [x, chroma, 0]
        : hue < 180
          ? [0, chroma, x]
          : hue < 240
            ? [0, x, chroma]
            : hue < 300
              ? [x, 0, chroma]
              : [chroma, 0, x];
  return `#${[red, green, blue]
    .map((channel) =>
      Math.round((channel + offset) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

export function hexToHslToken(value: string): string | null {
  const hex = normalizeHexColor(value);
  if (!hex) return null;
  const integer = Number.parseInt(hex.slice(1), 16);
  const channels = [
    ((integer >> 16) & 255) / 255,
    ((integer >> 8) & 255) / 255,
    (integer & 255) / 255,
  ];
  const maximum = Math.max(...channels);
  const minimum = Math.min(...channels);
  const delta = maximum - minimum;
  const lightness = (maximum + minimum) / 2;
  const saturation =
    delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;
  if (delta !== 0) {
    if (maximum === channels[0]) {
      hue = 60 * (((channels[1]! - channels[2]!) / delta) % 6);
    } else if (maximum === channels[1]) {
      hue = 60 * ((channels[2]! - channels[0]!) / delta + 2);
    } else {
      hue = 60 * ((channels[0]! - channels[1]!) / delta + 4);
    }
  }
  if (hue < 0) hue += 360;
  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(
    lightness * 100,
  )}%`;
}
